import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, Users, History, LogOut, Menu, X, Euro, Plus, Copy, 
  QrCode, Home, UserPlus, Send, Mail, Download, Eye, Trash2,
  TrendingUp, Coins, Calendar, MapPin, Phone, User, CheckCircle
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import Logo from '../components/Logo';

const CondoDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [condominiums, setCondominiums] = useState([]);
  const [selectedCondo, setSelectedCondo] = useState(null);
  const [residents, setResidents] = useState([]);
  const [inviteCodes, setInviteCodes] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [newCodeOpen, setNewCodeOpen] = useState(false);
  const [newCondoOpen, setNewCondoOpen] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(null);
  const [showCondoQR, setShowCondoQR] = useState(false);
  const [pendingResidents, setPendingResidents] = useState([]);
  const [residentDetail, setResidentDetail] = useState(null);
  const [residentTxs, setResidentTxs] = useState([]);
  const [residentHousehold, setResidentHousehold] = useState(null);
  const [residentTxsLoading, setResidentTxsLoading] = useState(false);
  const [allHouseholds, setAllHouseholds] = useState([]);
  const [linkHouseholdOpen, setLinkHouseholdOpen] = useState(false);
  const [selectedHouseholdId, setSelectedHouseholdId] = useState('');
  const [newCode, setNewCode] = useState({ validity_days: 30, max_uses: 10 });
  const [newCondo, setNewCondo] = useState({ name: '', address: '', total_units: '' });
  const [expenses, setExpenses] = useState([]);
  const [newExpenseOpen, setNewExpenseOpen] = useState(false);
  const [newExpense, setNewExpense] = useState({ user_id: '', description: '', period: '', amount_eur: '' });
  const [pointsCfg, setPointsCfg] = useState({ user_point_value: 0.07 });

  useEffect(() => {
    fetchCondominiums();
    api.get('/settings/points').then(r => setPointsCfg(r.data)).catch(() => {});
  }, []);


  const fetchCondominiums = async () => {
    try {
      const response = await api.get('/condominiums');
      setCondominiums(response.data);
      if (response.data.length > 0) {
        setSelectedCondo(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching condominiums:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCondoData = useCallback(async () => {
    try {
      const [usersRes, codesRes, transactionsRes, pendingRes, expensesRes] = await Promise.all([
        api.get(`/condominiums/${selectedCondo.id}/users`),
        api.get(`/invite-codes?condominium_id=${selectedCondo.id}`),
        api.get('/transactions'),
        api.get(`/condominiums/${selectedCondo.id}/pending-residents`),
        api.get('/condo/expenses', { params: { condominium_id: selectedCondo.id } }).catch(() => ({ data: [] })),
      ]);
      setResidents(usersRes.data);
      setInviteCodes(codesRes.data);
      setTransactions(transactionsRes.data);
      setPendingResidents(pendingRes.data);
      setExpenses(expensesRes.data || []);
    } catch (error) {
      console.error('Error fetching condo data:', error);
    }
  }, [selectedCondo]);

  useEffect(() => { if (selectedCondo) fetchCondoData(); }, [fetchCondoData , selectedCondo]);

  const handleCreateExpense = async () => {
    if (!newExpense.user_id || !newExpense.description.trim() || !newExpense.period.trim() || !newExpense.amount_eur) {
      toast.error('Compila tutti i campi');
      return;
    }
    const amount = parseFloat(newExpense.amount_eur);
    if (isNaN(amount) || amount <= 0) { toast.error('Importo non valido'); return; }
    try {
      await api.post('/condo/expenses', {
        condominium_id: selectedCondo.id,
        user_id: newExpense.user_id,
        description: newExpense.description.trim(),
        period: newExpense.period.trim(),
        amount_eur: amount,
      });
      toast.success('Spesa registrata');
      setNewExpenseOpen(false);
      setNewExpense({ user_id: '', description: '', period: '', amount_eur: '' });
      fetchCondoData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Eliminare questa spesa?')) return;
    try {
      await api.delete(`/condo/expenses/${expenseId}`);
      toast.success('Spesa eliminata');
      fetchCondoData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleApproveResident = async (userId) => {
    try {
      await api.post(`/residents/${userId}/approve?condominium_id=${selectedCondo.id}`);
      toast.success('Residente approvato');
      fetchCondoData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'approvazione');
    }
  };

  const handleRejectResident = async (userId) => {
    try {
      await api.post(`/residents/${userId}/reject?condominium_id=${selectedCondo.id}`);
      toast.success('Residente rifiutato');
      fetchCondoData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel rifiuto');
    }
  };

  const openResidentDetail = async (resident) => {
    setResidentDetail(resident);
    setResidentTxs([]);
    setResidentHousehold(null);
    setResidentTxsLoading(true);
    try {
      const [txRes, hhRes] = await Promise.all([
        api.get(`/users/${resident.id}/transactions`),
        api.get(`/users/${resident.id}/household`).catch(() => ({ data: null }))
      ]);
      setResidentTxs(txRes.data);
      setResidentHousehold(hhRes.data);
    } catch (e) {
      toast.error('Impossibile caricare i dettagli');
    } finally {
      setResidentTxsLoading(false);
    }
  };

  const handleMakeHead = async () => {
    if (!residentDetail) return;
    try {
      await api.post(`/users/${residentDetail.id}/make-household-head`);
      toast.success('Utente promosso a capo famiglia');
      fetchCondoData();
      openResidentDetail(residentDetail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const openLinkHouseholdDialog = async () => {
    try {
      const res = await api.get(`/condominiums/${selectedCondo.id}/households`);
      setAllHouseholds(res.data);
      setSelectedHouseholdId('');
      setLinkHouseholdOpen(true);
    } catch (error) {
      toast.error('Impossibile caricare i nuclei');
    }
  };

  const handleLinkHousehold = async () => {
    if (!residentDetail || !selectedHouseholdId) return;
    try {
      await api.post(`/users/${residentDetail.id}/assign-household`, null, { params: { household_id: selectedHouseholdId } });
      toast.success('Utente collegato al nucleo familiare');
      setLinkHouseholdOpen(false);
      fetchCondoData();
      openResidentDetail(residentDetail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel collegamento');
    }
  };

  const handleDetachHousehold = async () => {
    if (!residentDetail) return;
    if (!window.confirm('Vuoi scollegare l\'utente dal nucleo familiare?')) return;
    try {
      await api.post(`/users/${residentDetail.id}/detach-household`);
      toast.success('Utente scollegato');
      fetchCondoData();
      openResidentDetail(residentDetail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nello scollegamento');
    }
  };

  const handleCreateCondo = async () => {
    if (!newCondo.name || !newCondo.address) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    try {
      const response = await api.post('/condominiums', {
        name: newCondo.name,
        address: newCondo.address,
        total_units: parseInt(newCondo.total_units) || 0
      });
      toast.success('Condominio creato!');
      setNewCondoOpen(false);
      setNewCondo({ name: '', address: '', total_units: '' });
      fetchCondominiums();
      setSelectedCondo(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleCreateInviteCode = async () => {
    try {
      const response = await api.post('/invite-codes', {
        condominium_id: selectedCondo.id,
        validity_days: parseInt(newCode.validity_days),
        max_uses: parseInt(newCode.max_uses) || null
      });
      toast.success(`Codice creato: ${response.data.code}`);
      setNewCodeOpen(false);
      setNewCode({ validity_days: 30, max_uses: 10 });
      fetchCondoData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione del codice');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiato negli appunti!');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Calculate stats
  const totalResidents = residents.length;
  const totalPoints = residents.reduce((sum, r) => sum + (r.total_cashback || 0), 0);
  const totalTransactions = transactions.filter(t => t.status === 'confirmed').length;
  const totalSpent = transactions.filter(t => t.status === 'confirmed').reduce((sum, t) => sum + t.amount_spent, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="flex items-center gap-4">
              <Logo linkTo="/" size="sm" />
              <div className="border-l border-slate-200 pl-4">
                <h1 className="text-lg font-bold text-slate-900">Admin Condominio</h1>
                <p className="text-xs text-slate-500">{user?.name}</p>
              </div>
            </div>

            {/* Condominium Selector */}
            {condominiums.length > 1 && (
              <select
                value={selectedCondo?.id || ''}
                onChange={(e) => setSelectedCondo(condominiums.find(c => c.id === e.target.value))}
                className="hidden md:block px-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {condominiums.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Condominiums Selection (Mobile) */}
        {condominiums.length > 1 && (
          <div className="md:hidden mb-6">
            <Label>Seleziona Condominio</Label>
            <select
              value={selectedCondo?.id || ''}
              onChange={(e) => setSelectedCondo(condominiums.find(c => c.id === e.target.value))}
              className="w-full mt-1 px-4 py-3 border border-slate-200 rounded-lg text-sm"
            >
              {condominiums.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Current Condominium Info */}
        {selectedCondo && (
          <Card className="mb-6 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-emerald-800">{selectedCondo.name}</h2>
                  <div className="flex items-center gap-2 text-emerald-600 mt-1">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm">{selectedCondo.address}</span>
                  </div>
                  <div className="flex items-center gap-4 mt-3">
                    <Badge className="bg-emerald-200 text-emerald-800">
                      <Home className="w-3 h-3 mr-1" />
                      {selectedCondo.total_units || 0} unità
                    </Badge>
                    <Badge className="bg-teal-200 text-teal-800">
                      <Users className="w-3 h-3 mr-1" />
                      {totalResidents} residenti
                    </Badge>
                  </div>
                </div>
                <Dialog open={newCondoOpen} onOpenChange={setNewCondoOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="border-emerald-300 text-emerald-700">
                      <Plus className="w-4 h-4 mr-1" />
                      Nuovo Condominio
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Aggiungi Nuovo Condominio</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Nome Condominio *</Label>
                        <Input
                          placeholder="Es: Residenza Sole"
                          value={newCondo.name}
                          onChange={(e) => setNewCondo({ ...newCondo, name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Indirizzo *</Label>
                        <Input
                          placeholder="Es: Via Roma 1, Milano"
                          value={newCondo.address}
                          onChange={(e) => setNewCondo({ ...newCondo, address: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Numero Unità Abitative</Label>
                        <Input
                          type="number"
                          placeholder="Es: 50"
                          value={newCondo.total_units}
                          onChange={(e) => setNewCondo({ ...newCondo, total_units: e.target.value })}
                        />
                      </div>
                      <Button onClick={handleCreateCondo} className="w-full bg-emerald-600 hover:bg-emerald-700">
                        Crea Condominio
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{totalResidents}</p>
                  <p className="text-xs text-slate-500">Residenti</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Coins className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{totalPoints.toFixed(0)}</p>
                  <p className="text-xs text-slate-500">Punti Totali</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{totalTransactions}</p>
                  <p className="text-xs text-slate-500">Transazioni</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Euro className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">€{totalSpent.toFixed(0)}</p>
                  <p className="text-xs text-slate-500">Spese Totali</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="invite" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 w-full sm:w-auto justify-start overflow-x-auto flex-nowrap">
            <TabsTrigger value="invite" data-testid="invite-tab">
              <UserPlus className="w-4 h-4 mr-2" />
              Onboarding
            </TabsTrigger>
            <TabsTrigger value="pending" data-testid="pending-tab">
              <Mail className="w-4 h-4 mr-2" />
              Da Approvare {pendingResidents.length > 0 && `(${pendingResidents.length})`}
            </TabsTrigger>
            <TabsTrigger value="residents" data-testid="residents-tab">
              <Users className="w-4 h-4 mr-2" />
              Residenti
            </TabsTrigger>
            <TabsTrigger value="transactions" data-testid="transactions-tab">
              <History className="w-4 h-4 mr-2" />
              Transazioni
            </TabsTrigger>
            <TabsTrigger value="expenses" data-testid="condo-expenses-tab">
              <Euro className="w-4 h-4 mr-2" />
              Spese Condominiali
            </TabsTrigger>
          </TabsList>

          {/* Onboarding Tab */}
          <TabsContent value="invite">
            {/* SINGLE CONDO QR - primary method */}
            {selectedCondo && (
              <Card className="mb-6 border-2 border-emerald-400 bg-gradient-to-br from-emerald-50 to-teal-50">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row items-center gap-6">
                    <div className="p-4 bg-white rounded-2xl shadow-lg border flex-shrink-0">
                      <QRCodeSVG
                        value={`${window.location.origin}/register?condo=${selectedCondo.id}`}
                        size={180}
                        level="H"
                        data-testid="condo-single-qr"
                      />
                    </div>
                    <div className="flex-1">
                      <Badge className="bg-emerald-600 text-white mb-2">Consigliato</Badge>
                      <h3 className="text-xl font-bold text-emerald-900 mb-2">QR Unico del Condominio</h3>
                      <p className="text-sm text-slate-700 mb-4">
                        Stampa questo QR e affiggilo nell'androne: tutti i residenti potranno scansionarlo
                        per richiedere la registrazione. Ogni richiesta dovrà essere approvata da te nella sezione "Da Approvare".
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          onClick={() => copyToClipboard(`${window.location.origin}/register?condo=${selectedCondo.id}`)}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="copy-condo-link-btn"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copia link
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const text = `Unisciti a AIAC Card come residente: ${window.location.origin}/register?condo=${selectedCondo.id}`;
                            window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                          }}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Invia su WhatsApp
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowCondoQR(true)}
                          data-testid="expand-condo-qr-btn"
                        >
                          <QrCode className="w-4 h-4 mr-2" />
                          Ingrandisci
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <details className="mb-4">
              <summary className="cursor-pointer text-sm text-slate-500 hover:text-slate-700">
                Metodo alternativo: codici invito monouso/limitati
              </summary>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
              {/* Create Invite Code */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="w-5 h-5 text-emerald-600" />
                    Crea Codice Invito
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-slate-500">
                    Crea un codice invito per permettere ai residenti di registrarsi alla piattaforma.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Validità (giorni)</Label>
                      <Input
                        type="number"
                        value={newCode.validity_days}
                        onChange={(e) => setNewCode({ ...newCode, validity_days: e.target.value })}
                        min="1"
                        max="365"
                      />
                    </div>
                    <div>
                      <Label>Max Utilizzi</Label>
                      <Input
                        type="number"
                        value={newCode.max_uses}
                        onChange={(e) => setNewCode({ ...newCode, max_uses: e.target.value })}
                        placeholder="Illimitato"
                      />
                    </div>
                  </div>
                  <Button onClick={handleCreateInviteCode} className="w-full bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Genera Codice
                  </Button>
                </CardContent>
              </Card>

              {/* Existing Codes */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <QrCode className="w-5 h-5 text-emerald-600" />
                    Codici Attivi
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {inviteCodes.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <QrCode className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Nessun codice invito attivo</p>
                      <p className="text-sm">Crea un codice per iniziare l'onboarding</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {inviteCodes.map((code) => (
                        <div key={code.id} className="p-4 border border-slate-200 rounded-xl hover:bg-slate-50">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-mono font-bold text-lg text-emerald-700">{code.code}</p>
                              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                                <span>Utilizzi: {code.current_uses}/{code.max_uses || '∞'}</span>
                                <span>Scade: {new Date(code.expires_at).toLocaleDateString('it-IT')}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setShowQRDialog(code)}
                              >
                                <QrCode className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => copyToClipboard(code.code)}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            </details>

            {/* Quick Share Section */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="w-5 h-5 text-emerald-600" />
                  Condividi con i Residenti
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-4">
                  Usa questi strumenti per invitare i residenti a registrarsi sulla piattaforma AIAC Card.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {inviteCodes.length > 0 && (
                    <>
                      <Button 
                        variant="outline" 
                        className="h-auto py-4 flex-col"
                        onClick={() => {
                          const code = inviteCodes[0];
                          const text = `Ciao! Registrati su AIAC Card con il codice: ${code.code}\n\nLink: ${window.location.origin}/register?code=${code.code}`;
                          copyToClipboard(text);
                        }}
                      >
                        <Copy className="w-6 h-6 mb-2 text-slate-600" />
                        <span className="font-medium">Copia Messaggio</span>
                        <span className="text-xs text-slate-500">Con codice e link</span>
                      </Button>
                      <Button 
                        variant="outline" 
                        className="h-auto py-4 flex-col"
                        onClick={() => {
                          const code = inviteCodes[0];
                          const text = `Ciao! Registrati su AIAC Card con il codice: ${code.code}`;
                          const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
                          window.open(url, '_blank');
                        }}
                      >
                        <Send className="w-6 h-6 mb-2 text-green-600" />
                        <span className="font-medium">WhatsApp</span>
                        <span className="text-xs text-slate-500">Invia messaggio</span>
                      </Button>
                      <Button 
                        variant="outline" 
                        className="h-auto py-4 flex-col"
                        onClick={() => {
                          const code = inviteCodes[0];
                          const subject = 'Registrati su AIAC Card';
                          const body = `Ciao!\n\nSei invitato a registrarti su AIAC Card, la piattaforma di proposte del nostro condominio.\n\nCodice invito: ${code.code}\n\nLink registrazione: ${window.location.origin}/register?code=${code.code}\n\nBuon risparmio!`;
                          window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                        }}
                      >
                        <Mail className="w-6 h-6 mb-2 text-blue-600" />
                        <span className="font-medium">Email</span>
                        <span className="text-xs text-slate-500">Invia email</span>
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pending Residents Tab */}
          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-amber-600" />
                  Residenti in attesa di approvazione ({pendingResidents.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pendingResidents.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <UserPlus className="w-16 h-16 mx-auto mb-4 text-slate-200" />
                    <p className="font-medium">Nessuna richiesta in attesa</p>
                    <p className="text-sm">Le nuove richieste di registrazione appariranno qui</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingResidents.map((r) => (
                      <div key={r.id} className="flex items-center justify-between p-4 border border-amber-200 bg-amber-50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center">
                            <User className="w-5 h-5 text-amber-700" />
                          </div>
                          <div>
                            <p className="font-semibold">{r.name}</p>
                            <p className="text-sm text-slate-600">{r.email}</p>
                            <p className="text-xs text-slate-500">
                              Richiesta il {new Date(r.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-red-300 text-red-700 hover:bg-red-50"
                            onClick={() => handleRejectResident(r.id)}
                            data-testid={`reject-resident-${r.id}`}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Rifiuta
                          </Button>
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleApproveResident(r.id)}
                            data-testid={`approve-resident-${r.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approva
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Residents Tab */}
          <TabsContent value="residents">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Residenti Registrati ({residents.length})</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {residents.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Users className="w-16 h-16 mx-auto mb-4 text-slate-200" />
                    <p className="font-medium">Nessun residente registrato</p>
                    <p className="text-sm">Crea un codice invito per iniziare l'onboarding</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Nome</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Email</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Punti</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Registrato</th>
                        </tr>
                      </thead>
                      <tbody>
                        {residents.map((resident) => (
                          <tr 
                            key={resident.id} 
                            onClick={() => openResidentDetail(resident)}
                            className="border-b border-slate-100 hover:bg-emerald-50 cursor-pointer transition-colors"
                            data-testid={`resident-row-${resident.id}`}
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                                  <User className="w-4 h-4 text-emerald-600" />
                                </div>
                                <div className="flex flex-col">
                                  <span className="font-medium flex items-center gap-2">
                                    {resident.name}
                                    {resident.is_household_head && (
                                      <Badge className="bg-amber-200 text-amber-800 text-[10px] px-1.5 py-0">
                                        <Users className="w-3 h-3 mr-0.5" />Capo famiglia
                                      </Badge>
                                    )}
                                    {!resident.is_household_head && resident.household_id && (
                                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                        Membro famiglia
                                      </Badge>
                                    )}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-slate-600">{resident.email}</td>
                            <td className="py-3 px-4 text-right">
                              <Badge className="bg-amber-100 text-amber-700">
                                {resident.total_cashback?.toFixed(0) || 0} punti
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-slate-500 text-sm">
                              {new Date(resident.created_at).toLocaleDateString('it-IT')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <CardTitle>Storico Transazioni</CardTitle>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <History className="w-16 h-16 mx-auto mb-4 text-slate-200" />
                    <p className="font-medium">Nessuna transazione</p>
                    <p className="text-sm">Le transazioni dei residenti appariranno qui</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.map((tx) => (
                      <div key={tx.id} className="p-4 border border-slate-200 rounded-xl hover:bg-slate-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{tx.user_name}</p>
                            <p className="text-sm text-slate-500">{tx.offer_title} @ {tx.company_name}</p>
                            <p className="text-xs text-slate-400 mt-1">
                              {new Date(tx.created_at).toLocaleDateString('it-IT', { 
                                day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                              })}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-slate-500">Speso: €{tx.amount_spent.toFixed(2)}</p>
                            <p className="font-bold text-emerald-600">+{tx.cashback_net.toFixed(0)} punti</p>
                            <Badge variant={tx.status === 'confirmed' ? 'default' : tx.status === 'pending' ? 'outline' : 'secondary'}
                                   className={tx.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' : ''}>
                              {tx.status === 'confirmed' ? 'Confermata' : tx.status === 'pending' ? 'In attesa' : 'Rifiutata'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Condo Expenses Tab */}
          <TabsContent value="expenses">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Euro className="w-5 h-5 text-amber-600" />
                  Spese Condominiali dei Residenti
                </CardTitle>
                <Button
                  onClick={() => setNewExpenseOpen(true)}
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="new-expense-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Nuova Spesa
                </Button>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-4">
                  Registra le spese condominiali per ogni residente. Ogni residente potrà applicare i propri punti dal proprio account per abbattere il residuo dovuto.
                </p>
                {expenses.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Euro className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p>Nessuna spesa registrata.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {expenses.map((exp) => (
                      <div key={exp.id} className="p-3 border border-slate-200 rounded-lg" data-testid={`condo-expense-row-${exp.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-slate-800 truncate">{exp.user_name || exp.user_email}</p>
                            <p className="text-sm text-slate-600 truncate">{exp.description}</p>
                            <p className="text-xs text-slate-400">{exp.period}</p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <Badge className={
                              exp.status === 'paid' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                              exp.status === 'partially_paid' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                              'bg-slate-100 text-slate-700 border-slate-200'
                            }>
                              {exp.status === 'paid' ? 'Saldata' : exp.status === 'partially_paid' ? 'Parziale' : 'Da saldare'}
                            </Badge>
                            {exp.amount_paid_eur === 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteExpense(exp.id)}
                                data-testid={`delete-expense-${exp.id}`}
                                className="text-red-500 hover:text-red-700 h-7 px-2"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                          <div><p className="text-xs text-slate-500">Totale</p><p className="font-semibold">€{exp.amount_eur.toFixed(2)}</p></div>
                          <div><p className="text-xs text-slate-500">Punti applicati</p><p className="font-semibold text-emerald-700">{Math.round(exp.points_applied)} pt (€{exp.amount_paid_eur.toFixed(2)})</p></div>
                          <div><p className="text-xs text-slate-500">Residuo</p><p className="font-semibold text-amber-700">€{exp.amount_due_eur.toFixed(2)}</p></div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* New Expense Dialog */}
      <Dialog open={newExpenseOpen} onOpenChange={setNewExpenseOpen}>
        <DialogContent className="max-w-md" data-testid="new-expense-dialog">
          <DialogHeader>
            <DialogTitle>Nuova spesa condominiale</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Residente</Label>
              <select
                value={newExpense.user_id}
                onChange={(e) => setNewExpense({ ...newExpense, user_id: e.target.value })}
                data-testid="expense-user-select"
                className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md"
              >
                <option value="">Seleziona…</option>
                {residents.filter(r => (r.approval_status === 'approved' || !r.approval_status) && r.role === 'user' && !r.name?.startsWith('TEST_')).map((r) => (
                  <option key={r.id} value={r.id}>{r.name} — {r.email}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Descrizione</Label>
              <Input
                value={newExpense.description}
                onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                placeholder="Es. Rata trimestrale ordinaria"
                data-testid="expense-description"
              />
            </div>
            <div>
              <Label>Periodo</Label>
              <Input
                value={newExpense.period}
                onChange={(e) => setNewExpense({ ...newExpense, period: e.target.value })}
                placeholder="Es. 2026-Q1 oppure Gennaio 2026"
                data-testid="expense-period"
              />
            </div>
            <div>
              <Label>Importo (€)</Label>
              <Input
                type="number"
                min="1"
                step="0.01"
                value={newExpense.amount_eur}
                onChange={(e) => setNewExpense({ ...newExpense, amount_eur: e.target.value })}
                placeholder="150.00"
                data-testid="expense-amount"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setNewExpenseOpen(false)}>Annulla</Button>
              <Button onClick={handleCreateExpense} data-testid="save-expense-btn" className="bg-amber-500 hover:bg-amber-600 text-white">Salva</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={!!showQRDialog} onOpenChange={() => setShowQRDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">Codice Invito QR</DialogTitle>
          </DialogHeader>
          {showQRDialog && (
            <div className="flex flex-col items-center py-6">
              <div className="p-4 bg-white rounded-2xl shadow-lg border">
                <QRCodeSVG 
                  value={`${window.location.origin}/register?code=${showQRDialog.code}`}
                  size={200}
                  level="H"
                />
              </div>
              <p className="mt-4 font-mono text-2xl font-bold text-emerald-600">{showQRDialog.code}</p>
              <p className="text-sm text-slate-500 mt-2 text-center">
                Scansiona o usa il codice per registrarti
              </p>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => copyToClipboard(showQRDialog.code)}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copia Codice
                </Button>
                <Button variant="outline" onClick={() => {
                  copyToClipboard(`${window.location.origin}/register?code=${showQRDialog.code}`);
                }}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copia Link
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Large Condo QR Dialog (printable) */}
      <Dialog open={showCondoQR} onOpenChange={setShowCondoQR}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">QR Unico del Condominio</DialogTitle>
          </DialogHeader>
          {selectedCondo && (
            <div className="flex flex-col items-center py-4">
              <div className="p-6 bg-white rounded-2xl shadow-lg border-2 border-emerald-200">
                <QRCodeSVG
                  value={`${window.location.origin}/register?condo=${selectedCondo.id}`}
                  size={280}
                  level="H"
                />
              </div>
              <p className="mt-4 font-bold text-lg text-emerald-800 text-center">{selectedCondo.name}</p>
              <p className="text-xs text-slate-500 mt-1 text-center">
                I residenti scansionano questo QR per richiedere l'iscrizione
              </p>
              <Button
                className="mt-4 bg-emerald-600 hover:bg-emerald-700"
                onClick={() => window.print()}
              >
                <Download className="w-4 h-4 mr-2" />
                Stampa
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Resident Detail Dialog */}
      <Dialog open={!!residentDetail} onOpenChange={(o) => { if (!o) { setResidentDetail(null); setResidentTxs([]); setResidentHousehold(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dettaglio Residente</DialogTitle>
          </DialogHeader>
          {residentDetail && (
            <div className="space-y-5">
              {/* Header */}
              <div className="flex items-start gap-4 p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200">
                <div className="w-14 h-14 rounded-full bg-emerald-200 flex items-center justify-center shrink-0">
                  <User className="w-7 h-7 text-emerald-700" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-bold text-emerald-900 truncate">{residentDetail.name}</h3>
                  <p className="text-sm text-emerald-700 truncate">{residentDetail.email}</p>
                  {residentDetail.phone && <p className="text-xs text-emerald-600">{residentDetail.phone}</p>}
                  <p className="text-xs text-slate-500 mt-1">
                    Registrato il {new Date(residentDetail.created_at).toLocaleDateString('it-IT')}
                  </p>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
                  <p className="text-xs text-amber-700">Punti totali</p>
                  <p className="text-2xl font-bold text-amber-800">{Math.round(residentDetail.total_cashback || 0)}</p>
                  <p className="text-xs text-amber-600">= €{((residentDetail.total_cashback || 0) * (pointsCfg.user_point_value || 0.07)).toFixed(2)}</p>
                </div>
                <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                  <p className="text-xs text-emerald-700">Confermate</p>
                  <p className="text-2xl font-bold text-emerald-800">{residentTxs.filter(t => t.status === 'confirmed').length}</p>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                  <p className="text-xs text-slate-600">Spesa totale</p>
                  <p className="text-2xl font-bold text-slate-800">€{residentTxs.filter(t => t.status === 'confirmed').reduce((s, t) => s + (t.amount_spent || 0), 0).toFixed(0)}</p>
                </div>
              </div>

              {/* Household / Famiglia */}
              {residentHousehold ? (
                <div className="p-4 rounded-xl border-2 border-amber-200 bg-amber-50">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-amber-700" />
                      <p className="font-semibold text-amber-900">{residentHousehold.name}</p>
                    </div>
                    <Badge className="bg-amber-600 text-white">
                      {Math.round(residentHousehold.total_points || 0)} punti famiglia
                    </Badge>
                  </div>
                  <p className="text-xs text-amber-700 mb-3">
                    Capo famiglia: <span className="font-semibold">{residentHousehold.head_name}</span> ·
                    Codice referral: <span className="font-mono">{residentHousehold.referral_code}</span>
                  </p>
                  <div className="space-y-2">
                    {residentHousehold.members?.map((m) => (
                      <div key={m.id} className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-amber-100">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                            <User className="w-4 h-4 text-amber-700" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate flex items-center gap-2">
                              {m.name}
                              {m.is_head && <Badge className="bg-amber-200 text-amber-800 text-[10px] px-1.5 py-0">Capo</Badge>}
                              {m.approval_status === 'pending' && <Badge className="bg-orange-200 text-orange-800 text-[10px] px-1.5 py-0">In attesa</Badge>}
                            </p>
                            <p className="text-xs text-slate-500 truncate">{m.email}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="shrink-0">
                          {Math.round(m.total_cashback || 0)} pt
                        </Badge>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-amber-200">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-red-300 text-red-700 hover:bg-red-50"
                      onClick={handleDetachHousehold}
                      data-testid="detach-household-btn"
                    >
                      <X className="w-4 h-4 mr-1" /> Scollega dalla famiglia
                    </Button>
                    {residentDetail.is_household_head && (
                      <span className="text-xs text-amber-700 ml-3 italic">
                        (è possibile solo se il capo non ha altri membri collegati)
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50">
                  <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    <Users className="w-5 h-5 text-slate-500" />
                    Nessun nucleo familiare
                  </p>
                  <p className="text-xs text-slate-500 mt-1 mb-3">
                    L'utente non fa parte di una famiglia. Puoi promuoverlo a capo famiglia o collegarlo a un nucleo esistente.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-700"
                      onClick={handleMakeHead}
                      data-testid="make-head-btn"
                    >
                      <Users className="w-4 h-4 mr-1" /> Rendi capo famiglia
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={openLinkHouseholdDialog}
                      data-testid="link-household-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-1" /> Collega a famiglia esistente
                    </Button>
                  </div>
                </div>
              )}

              {/* Transactions */}
              <div>
                <p className="text-sm font-semibold text-slate-900 mb-2">Storico transazioni</p>
                {residentTxsLoading ? (
                  <div className="py-6 text-center text-slate-500 text-sm">Caricamento…</div>
                ) : residentTxs.length === 0 ? (
                  <div className="py-6 text-center text-slate-500 text-sm">Nessuna transazione</div>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {residentTxs.map((tx) => (
                      <div key={tx.id} className="p-3 border border-slate-200 rounded-lg flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">{tx.offer_title}</p>
                          <p className="text-xs text-slate-500 truncate">{tx.company_name}</p>
                          <p className="text-xs text-slate-400">{new Date(tx.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-semibold">€{tx.amount_spent.toFixed(2)}</p>
                          <p className="text-xs text-emerald-600">+{tx.cashback_net.toFixed(0)} pt</p>
                          <Badge className={
                            tx.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' :
                            tx.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }>
                            {tx.status === 'confirmed' ? 'Confermata' : tx.status === 'pending' ? 'In attesa' : 'Rifiutata'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Link to existing Household Dialog */}
      <Dialog open={linkHouseholdOpen} onOpenChange={(o) => { setLinkHouseholdOpen(o); if (!o) setSelectedHouseholdId(''); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Collega a famiglia esistente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Seleziona il nucleo familiare a cui collegare <strong>{residentDetail?.name}</strong>:
            </p>
            {allHouseholds.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">
                Nessun nucleo familiare disponibile nel condominio.
              </p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {allHouseholds.map((hh) => (
                  <label
                    key={hh.household_id}
                    className={`flex items-center justify-between gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedHouseholdId === hh.household_id ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <input
                        type="radio"
                        name="household"
                        value={hh.household_id}
                        checked={selectedHouseholdId === hh.household_id}
                        onChange={(e) => setSelectedHouseholdId(e.target.value)}
                        className="accent-amber-600"
                        data-testid={`radio-hh-${hh.household_id}`}
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{hh.name}</p>
                        <p className="text-xs text-slate-500 truncate">
                          Capo: {hh.head_name} · {hh.members_count} membri
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="shrink-0">
                      {Math.round(hh.total_points || 0)} pt
                    </Badge>
                  </label>
                ))}
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setLinkHouseholdOpen(false)}>
                Annulla
              </Button>
              <Button
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                disabled={!selectedHouseholdId}
                onClick={handleLinkHousehold}
                data-testid="confirm-link-household-btn"
              >
                Collega
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CondoDashboard;
